// frontend/src/pages/MyPoints.js
// Purpose: Scholar self-view of their own awarded points.
// Shows: Summary (Total Earned + Available to Spend) and a table of Date, Reason, Awarded By.
//
// Logic: Total Points Earned = number of table rows.
// Balance: Prefer backend /api/students/me/balance (or /:id/balance), fallback = row count.
//
// UPDATE (backend refactor compatibility):
// - /api/students/me/history now 404s in refactored backend.
// - Use /api/events/ as the primary history feed.
// - If an educator/admin lands here, redirect them to /dashboard.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

/* Tiny breakpoint hook */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/* Format a timestamp-ish value to a readable short date + time */
function fmtWhen(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
}

/* Normalize a history row (supports old student-history rows and new events rows) */
function normalizeRow(it = {}) {
  const when =
    it.created_at ||
    it.timestamp ||
    it.awarded_at ||
    it.date ||
    it.when ||
    it.time;

  const reason =
    it.reason_text ||
    it.reason ||
    it.description ||
    it.habit_reason ||
    it.habit_text ||
    // events model often has "reason" as plain string
    it.source ||
    it.habit ||
    "";

  const educator =
    it.awarded_by?.full_name ||
    it.awarded_by?.name ||
    it.awarded_by_name ||
    it.educator_name ||
    it.teacher_name ||
    it.awarded_by ||
    // events model may have created_by as an id
    (it.created_by ? `Staff #${it.created_by}` : "") ||
    "";

  return { when, reason, educator };
}

/* Prefer backend summary */
async function fetchBalance({ studentId = null } = {}) {
  const paths = ["/api/students/me/balance"];
  if (studentId) paths.push(`/api/students/${studentId}/balance`);

  for (const p of paths) {
    const res = await api.get(p);
    if (res?.ok && res.data) {
      const d = res.data;
      const available =
        Number(d.available ?? d.balance ?? d.points ?? d.remaining ?? 0);
      if (Number.isFinite(available)) {
        return { available };
      }
    }
  }
  return null;
}

function isStudentDomain(emailOrUsername) {
  const s = String(emailOrUsername || "").trim().toLowerCase();
  return /@student\.propelschools\.org$/i.test(s);
}

function isEducatorUser(me) {
  const role = String(me?.role || "").trim().toUpperCase();
  const staffish =
    !!me?.is_staff ||
    !!me?.is_superuser ||
    role === "ADMIN" ||
    role === "STAFF";
  const educatorish =
    role === "EDUCATOR" ||
    role === "TEACHER" ||
    role === "ADMIN" ||
    role === "STAFF" ||
    staffish;

  const email = me?.email || "";
  const username = me?.username || "";
  const studentDomain = isStudentDomain(email) || isStudentDomain(username);

  return educatorish && !studentDomain;
}

// Robust fetch for "my history" across backends
async function fetchMyHistory({ studentId = null } = {}) {
  // Refactored backend uses /api/events/ (likely returns only "my" events for scholars).
  // Keep old endpoints as optional fallbacks in case they exist in other deployments.
  const candidates = [
    // NEW backend (preferred)
    studentId ? `/api/events/?student_id=${encodeURIComponent(String(studentId))}` : null,
    "/api/events/",
    "/api/events",

    // OLD backend fallbacks (may 404; safe to try)
    "/api/students/me/history",
    studentId ? `/api/students/${studentId}/history` : null,
  ].filter(Boolean);

  for (const p of candidates) {
    const res = await api.get(p);
    if (!res?.ok) continue;

    // Support payload shapes: array, {results:[]}, {data:[]}
    const d = res.data;
    const items =
      Array.isArray(d) ? d :
      Array.isArray(d?.results) ? d.results :
      Array.isArray(d?.data) ? d.data :
      [];

    return { ok: true, items, hit: p, raw: d };
  }

  return { ok: false, items: [], hit: null };
}

export default function MyPoints() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");

  // Summary state
  const [totalEarned, setTotalEarned] = useState(0);
  const [available, setAvailable] = useState(0);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");

      const finishWith = async (items, studentId = null) => {
        if (!mounted) return;
        const rowCount = Array.isArray(items) ? items.length : 0;

        setHistory(items);
        setTotalEarned(rowCount);

        const bal = await fetchBalance({ studentId });
        if (!mounted) return;
        if (bal) setAvailable(bal.available);
        else setAvailable(rowCount);

        setLoading(false);
      };

      // Always get /me first now so we can:
      //  - redirect educator/admin away from scholar page
      //  - determine studentId (if backend provides it)
      const meRes = await api.get("/api/auth/me");
      if (!meRes?.ok) {
        if (mounted) {
          setError(meRes?.data?.message || meRes?.data?.detail || "Unable to load your account.");
          setLoading(false);
        }
        return;
      }

      const me = meRes.data || {};

      // Prevent educators/admins from living on scholar page
      if (isEducatorUser(me)) {
        navigate("/dashboard", { replace: true });
        return;
      }

      const role = String(me.role || "").toUpperCase();

      // Try to infer studentId if present (keep your existing logic)
      const studentId =
        me.student?.id ||
        me.scholar?.id ||
        ((role === "SCHOLAR" || role === "STUDENT") ? me.id : null);

      // Fetch history (new backend prefers /api/events/)
      const hist = await fetchMyHistory({ studentId });
      if (hist.ok) {
        await finishWith(hist.items, studentId);
        return;
      }

      // If we couldn't fetch history, show error
      if (mounted) {
        setError("Unable to load your points history.");
        setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const s = styles(isMobile);

  return (
    <div style={s.page}>
      <h1 style={s.h1}>My Points</h1>

      <div style={s.card}>
        {loading && <div>Loading…</div>}

        {!loading && error && (
          <div style={s.error} role="alert">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Summary Row */}
            <div style={s.summaryRow} aria-label="My points summary">
              <div style={s.summaryItem}>
                <div style={s.summaryLabel}>Total Points Earned</div>
                <div style={s.summaryBadge}>{totalEarned}</div>
              </div>
              <div style={s.separator} aria-hidden="true" />
              <div style={s.summaryItem}>
                <div style={s.summaryLabel}>Points Available to Spend</div>
                <div style={s.summaryBadge}>{available}</div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={s.table} aria-label="My points history">
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Reason</th>
                    <th style={s.th}>Awarded By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: 12 }}>
                        No points yet.
                      </td>
                    </tr>
                  )}

                  {history.map((raw, idx) => {
                    const row = normalizeRow(raw);
                    return (
                      <tr key={raw.id ?? idx}>
                        <td style={s.td}>{fmtWhen(row.when)}</td>
                        <td style={s.td}>{row.reason || "—"}</td>
                        <td style={s.td}>{row.educator || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function styles(isMobile) {
  const navy = "#003e73";
  return {
    page: { display: "grid", gap: 16 },
    h1: { margin: 0, fontSize: 22, color: navy, fontWeight: 800, textAlign: "center" },

    card: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 12,
      padding: isMobile ? 10 : 16,
      display: "grid",
      gap: 16,
      boxShadow: "0 3px 8px rgba(0,0,0,0.05)",
    },

    error: {
      color: "#b00020",
      background: "#fde7ea",
      border: "1px solid rgba(176,0,32,0.2)",
      padding: 12,
      borderRadius: 10,
      marginBottom: 8,
      fontWeight: 700,
    },

    summaryRow: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1px 1fr",
      alignItems: "stretch",
      border: "1px solid rgba(0,0,0,0.06)",
      borderRadius: 12,
      overflow: "hidden",
      background: "#f7f9fc",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
    },
    summaryItem: {
      padding: 16,
      display: "grid",
      justifyItems: "center",
      alignItems: "center",
      gap: 6,
      background: "#fff",
    },
    separator: {
      width: 1,
      background: "rgba(0,0,0,0.08)",
      display: isMobile ? "none" : "block",
    },
    summaryLabel: {
      fontSize: 12,
      letterSpacing: 0.2,
      textTransform: "uppercase",
      color: "#5a6b7b",
      fontWeight: 800,
    },
    summaryBadge: {
      minWidth: 80,
      textAlign: "center",
      padding: "6px 14px",
      fontSize: 24,
      fontWeight: 900,
      color: "#fff",
      background: navy,
      borderRadius: 12,
      boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
    },

    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      fontSize: isMobile ? 13 : 14,
    },
    th: {
      textAlign: "left",
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background: "#f7f9fc",
      whiteSpace: "nowrap",
      color: navy,
      fontWeight: 800,
    },
    td: {
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      whiteSpace: "nowrap",
    },
  };
}
